import { Form, Formik } from "formik";
import React, { FC, useCallback, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { OldPreset } from "../../../models/OldPreset";
import { getOutputPresetById } from "../../../services/presetOutputService";
import { showLoadItemError } from "../../../services/utilities/ServiceError";
import { useDialog } from "../../dialog/DialogContext";
import Wizard from "../../wizard/wizard";
import { WizardSummaryContext } from "../../wizard/wizardSummaryContext";
import { WizardContent } from "./wizardContent";
import { WizardSummary } from "./wizardSummary";

interface PresetOutputWizardProps {
    presetId?: string;
    isOpen: boolean;
    title: string;
    onClose: () => void;
    onSave: (formFields, createNew) => void;
    profileId: string
}

export const PresetOutputWizard: FC<PresetOutputWizardProps> = ({ presetId = '', isOpen, title, onClose, onSave, profileId }) => {
    /* Used for provider to gather up the displayable values of the form's fields.
        This is done specifically for select fields, where the label is not the stored value, and the selected value is an id.*/
    const [summary, setSummary] = useState(new Array(0));
    const [step, setStep] = useState(0);
    const { translate } = useLocale();
    const [wizardTitle, setWizardTitle] = useState<string>(title);
    const dialog = useDialog();
    const initialSidebarSteps = [{
        title: translate.byKey(TranslationKeys.details),
        description: '',
        showToolTip: false,
        toolTipDescription: '',
        substeps: []
    }, {
        title: translate.byKey(TranslationKeys.summary),
        description: '',
        showToolTip: false,
        toolTipDescription: '',
        substeps: []
    }];
    const [sidebarSteps, setSidebarSteps] = useState(initialSidebarSteps);
    const [supportedFields, setSupportedFields] = useState(new Array<any>(0));
    const initialValues = {
        id: 0,
        name: '',
        type: '',
        typeId: 0,
        usedBy: '',
        custom: {},
    };
    const [currentItem, setCurrentItem] = useState<OldPreset>(initialValues);
    const [modeName, setModeName] = useState<string>('');

    useEffect(() => {
        reloadPage();
    }, [isOpen === true])

    const reloadPage = async () => {
        setSummary(new Array(0));
        if (!!presetId) {
            getOutputPresetById(presetId, profileId)
                .then(preset => {
                    const currentItem = preset.customData
                    setCurrentItem({ ...preset, custom: currentItem });
                    /** Might not be the best sorting ideal but we can't sort using ordinal as these are using the ordinal of the setting type which is the same*/
                    preset.elementFields.sort((a, b) => a.settingDataId - b.settingDataId)
                    setSupportedFields(preset.elementFields);
                    setModeName(preset.preset.name)
                    setWizardTitle(title + ": " + preset.preset.name);
                }).catch(error => {
                    showLoadItemError(error, dialog, translate, handleClose);
                    return;
                });
        }
        setCurrentItem(initialValues);
        setModeName('');
        setWizardTitle(title);
        setSupportedFields(new Array<any>(0));
        setSidebarSteps(initialSidebarSteps);
    }

    const handleClose = () => {
        setSidebarSteps(initialSidebarSteps);
        setStep(0);
        onClose();
    }

    const handleSubmit = (formFields) => {
        onSave(formFields, (+presetId <= 0));
        handleClose();
    };

    const handleWizardFirstNext: ((event: any) => void | undefined) | undefined = (event) => {
        const newFirstSidebarStep = { ...sidebarSteps[0], description: event.itemName, toolTipDescription: event.toolTipDescription, showToolTip: event.showToolTip };
        setSidebarSteps([newFirstSidebarStep].concat(...sidebarSteps.slice(1)));
        setStep(step + 1);
    };
    const contentPages = [
        <WizardContent
            key={"wizard-1"}
            itemId={presetId}
            onNext={handleWizardFirstNext}
            onBack={handleClose}
            supportedFields={supportedFields}>
        </WizardContent>,
        <WizardSummary
            key={"wizard-2"}
            onBack={() => setStep(step - 1)}
            supportedFields={supportedFields}
            modeName={modeName}>
        </WizardSummary>
    ];

    const updateSummary = (accessor: string, newDisplayValue: string) => {
        if (summary) {
            const existingSummaryEntryIndex = summary.findIndex(item => item.value === accessor);
            const newSummaryEntry = { value: accessor, label: newDisplayValue };
            if (existingSummaryEntryIndex >= 0) {
                summary.splice(existingSummaryEntryIndex, 1, newSummaryEntry);
            }
            else {
                summary.push(newSummaryEntry);
            }
        }
    }

    const getContextValue = useCallback(
        () => (
            {
                summary: [...summary],
                updateSummary
            }
        ),
        [summary, setSummary],
    )

    return <WizardSummaryContext.Provider value={getContextValue()}>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <Formik
                initialValues={currentItem}
                onSubmit={handleSubmit}
                enableReinitialize={true}>
                <Form>
                    <Wizard
                        name={wizardTitle}
                        onClose={handleClose}
                        step={step}
                        sidebarSteps={sidebarSteps}>
                        {contentPages}
                    </Wizard>
                </Form>
            </Formik>
        </Modal>
    </WizardSummaryContext.Provider>;
};
